import {
	ZOOM_LANES,
	ZOOM_START_SPEED,
	ZOOM_TOP_SPEED,
	type ZoomInput,
	type ZoomState,
	zoomAhead,
	zoomHop,
	zoomLaneAt,
	zoomSpeed,
} from "@playpal/core";
import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import { useGestures } from "../lib/useGestures";

/**
 * How many rows of tunnel are on screen at once.
 *
 * The same for both children whatever they are playing on, because seeing
 * further ahead is seeing more of the track, and a race where one player can
 * see two obstacles and the other can see four is not a race. It is also why
 * this board has a fixed shape at all when a 3D camera could happily fill any
 * screen given to it.
 */
export const ZOOM_DRAW_ROWS = 16;

/** The world's width over its height — what the stage scales to fit. */
export const ZOOM_ASPECT = 3 / 4;

/**
 * The WebGL tunnel, in its own chunk.
 *
 * Lazy is load-bearing rather than tidy. three.js is about a hundred and thirty
 * kilobytes gzipped, which is most of the rest of PlayPal put together, and
 * forty-eight of the forty-nine games have no use for it whatsoever. Behind a
 * dynamic import it is fetched the first time a child opens this one game and
 * never by anybody who does not.
 */
const ZoomScene = lazy(() => import("./ZoomScene"));

interface Props {
	state: ZoomState;
	onPress: (input: ZoomInput) => void;
	/** False before the run starts and after it ends — taps stop working then. */
	live: boolean;
}

export function ZoomField({ state, onPress, live }: Props) {
	const surface = useRef<HTMLDivElement>(null);

	useGestures(surface, {
		onSwipe: (direction) => {
			if (direction === "left") onPress({ move: "left" });
			else if (direction === "right") onPress({ move: "right" });
			else if (direction === "up") onPress({ jump: true });
		},
		// A tap is the jump as well as a swipe up, because a thumb in a hurry
		// flicks about four pixels and that is not a swipe by any measure.
		onTap: () => onPress({ jump: true }),
		enabled: live,
	});
	useKeys(onPress, live);

	const solid = useWebGl();

	// How close to top speed the run is, 0 to 1 — what the speed lines fade in
	// on. Derived from the step, so it costs nothing to know.
	const pace = Math.min(
		1,
		Math.max(0, (zoomSpeed(state.step) - ZOOM_START_SPEED) / (ZOOM_TOP_SPEED - ZOOM_START_SPEED)),
	);

	return (
		<div
			ref={surface}
			data-testid="zoom-field"
			// Not a button: there are three things to do here and a single
			// control with three meanings is a control a screen reader cannot
			// describe. The buttons underneath are the accessible way in.
			aria-hidden="true"
			className="relative block w-full touch-none select-none overflow-hidden rounded-3xl bg-slate-950 shadow-inner ring-4 ring-indigo-500/40"
			style={{ aspectRatio: `${ZOOM_ASPECT}` }}
		>
			{solid ? (
				// Until the chunk arrives, the flat tunnel — which is a playable
				// game rather than a spinner, so a slow connection costs a child
				// some scenery and not their go.
				<Suspense fallback={<ZoomFlat state={state} />}>
					<ZoomScene state={state} />
				</Suspense>
			) : (
				<ZoomFlat state={state} />
			)}

			{/* A soft vignette pulls the eye to the middle of the tunnel. Plain
			    CSS over the mount — nothing here touches the renderer. */}
			<div
				className="pointer-events-none absolute inset-0 rounded-3xl"
				style={{ boxShadow: "inset 0 0 60px 24px rgb(2 6 23 / 0.55)" }}
			/>

			{/* Speed lines down the edges once the run winds up, fading in with
			    pace. Four 2px elements, animated with one looping transform. */}
			{pace > 0.15 && !state.over && (
				<div className="pointer-events-none absolute inset-0" style={{ opacity: pace * 0.7 }}>
					<div className="zoom-speed-line left-[5%]" />
					<div className="zoom-speed-line left-[10%]" style={{ animationDelay: "-0.2s" }} />
					<div className="zoom-speed-line right-[5%]" style={{ animationDelay: "-0.1s" }} />
					<div className="zoom-speed-line right-[10%]" style={{ animationDelay: "-0.3s" }} />
				</div>
			)}
		</div>
	);
}

/**
 * The tunnel drawn with boxes, for anything that cannot give us WebGL.
 *
 * Not a placeholder. An old tablet, a locked-down school device or a browser
 * with hardware acceleration switched off will land here, and a child on one of
 * those should get the game rather than an apology. It is also what the tests
 * see, which is the more useful half of the bargain: the scene graph is
 * untestable in jsdom, and everything worth asserting about what is drawn — how
 * many obstacles, in which lanes, how far off — is asserted here instead.
 */
export function ZoomFlat({ state }: { state: ZoomState }) {
	const tiles = zoomAhead(state, ZOOM_DRAW_ROWS);
	const lane = zoomLaneAt(state);
	const hop = zoomHop(state.air);

	/** Lane number to a share of the width, as a percentage of the left edge. */
	const across = (at: number) => `${(at / ZOOM_LANES) * 100}%`;
	/** Rows ahead to a share of the height, with the runner at the bottom. */
	const along = (depth: number) => `${Math.max(0, 1 - depth / ZOOM_DRAW_ROWS) * 100}%`;

	return (
		<div data-testid="zoom-flat" className="absolute inset-0">
			{/* The void: a night sky deepening towards the far end of the track,
			    with a scatter of fixed stars. One element, all gradients. */}
			<div
				className="absolute inset-0"
				style={{
					background: [
						"radial-gradient(1px 1px at 12% 8%, rgb(255 255 255 / 0.8), transparent 100%)",
						"radial-gradient(1px 1px at 78% 5%, rgb(255 255 255 / 0.6), transparent 100%)",
						"radial-gradient(1.5px 1.5px at 30% 16%, rgb(191 219 254 / 0.7), transparent 100%)",
						"radial-gradient(1px 1px at 55% 11%, rgb(255 255 255 / 0.5), transparent 100%)",
						"radial-gradient(1.5px 1.5px at 90% 20%, rgb(199 210 254 / 0.6), transparent 100%)",
						"radial-gradient(1px 1px at 42% 3%, rgb(255 255 255 / 0.7), transparent 100%)",
						"radial-gradient(120% 45% at 50% 0%, rgb(49 46 129 / 0.55) 0%, transparent 70%)",
						"linear-gradient(180deg, #1e1b4b 0%, #0f172a 45%, #020617 100%)",
					].join(", "),
				}}
			/>

			{/* Lane edges, brightest under the runner and gone at the horizon. */}
			{[1, 2].map((edge) => (
				<div
					key={edge}
					className="absolute top-0 bottom-0 w-px"
					style={{
						left: across(edge),
						background: "linear-gradient(180deg, transparent 5%, rgb(129 140 248 / 0.45))",
					}}
				/>
			))}

			{tiles.map((tile) =>
				[0, 1, 2].map((at) => {
					const walled = (tile.walls & (1 << at)) !== 0;
					const barred = (tile.bars & (1 << at)) !== 0;
					const gem = tile.gem === at && !tile.taken;
					if (!walled && !barred && !gem) return null;

					return (
						<div
							key={`${tile.row}:${at}`}
							data-testid={walled ? "zoom-wall" : barred ? "zoom-bar" : "zoom-gem"}
							data-row={tile.row}
							data-lane={at}
							className={
								walled
									? "absolute rounded-md bg-gradient-to-b from-rose-400 to-rose-600 shadow-[0_0_10px_rgb(244_63_94/0.5)] ring-1 ring-rose-300/50"
									: barred
										? "absolute rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 shadow-[0_0_8px_rgb(245_158_11/0.5)] ring-1 ring-amber-200/50"
										: "absolute rotate-45 rounded-[20%] bg-gradient-to-br from-cyan-200 to-cyan-400 shadow-[0_0_12px_3px_rgb(34_211_238/0.6)]"
							}
							style={{
								left: across(at + (gem ? 0.38 : 0.08)),
								width: `${(gem ? 0.24 : 0.84) * (100 / ZOOM_LANES)}%`,
								top: along(tile.depth),
								// Nearer things are drawn bigger, which is the only
								// depth cue a flat board has to offer.
								height: walled ? "5%" : "2.5%",
								opacity: Math.max(0.25, 1 - tile.depth / ZOOM_DRAW_ROWS),
							}}
						/>
					);
				}),
			)}

			<div
				data-testid="zoom-runner"
				data-lane={state.lane}
				data-airborne={state.air > 0 ? "yes" : "no"}
				className="absolute rounded-full shadow-[0_0_14px_4px_rgb(148_163_253/0.45)]"
				style={{
					left: across(lane + 0.28),
					width: `${0.44 * (100 / ZOOM_LANES)}%`,
					height: "6%",
					// Up the screen a little while airborne, which is as much as a
					// flat picture can say about being off the floor.
					bottom: `${4 + hop * 9}%`,
					background: "radial-gradient(circle at 35% 30%, #ffffff 0%, #e2e8f0 60%, #94a3b8 100%)",
				}}
			/>
		</div>
	);
}

/**
 * Left, jump, right — the accessible way to play, and the only way on a laptop
 * without a keyboard to hand.
 */
export function ZoomControls({
	onPress,
	live,
}: {
	onPress: (input: ZoomInput) => void;
	live: boolean;
}) {
	const press = (input: ZoomInput) => (event: React.PointerEvent) => {
		// Pointer down rather than click: at thirty steps a second the wait for
		// a click to be confirmed is most of a lane change.
		event.preventDefault();
		if (live) onPress(input);
	};

	return (
		<div className="grid grid-cols-3 gap-2 pb-1">
			<button
				type="button"
				aria-label="Go left"
				onPointerDown={press({ move: "left" })}
				className="rounded-2xl bg-white/10 py-4 text-3xl leading-none active:bg-white/20"
			>
				<span aria-hidden="true">◀️</span>
			</button>
			<button
				type="button"
				aria-label="Jump"
				onPointerDown={press({ jump: true })}
				className="rounded-2xl bg-white/10 py-4 text-3xl leading-none active:bg-white/20"
			>
				<span aria-hidden="true">⬆️</span>
			</button>
			<button
				type="button"
				aria-label="Go right"
				onPointerDown={press({ move: "right" })}
				className="rounded-2xl bg-white/10 py-4 text-3xl leading-none active:bg-white/20"
			>
				<span aria-hidden="true">▶️</span>
			</button>
		</div>
	);
}

/** Arrow keys and space, for whoever is playing on a laptop. */
function useKeys(onPress: (input: ZoomInput) => void, live: boolean) {
	useEffect(() => {
		if (!live) return;

		const onKeyDown = (event: KeyboardEvent) => {
			const input: ZoomInput | null =
				event.key === "ArrowLeft"
					? { move: "left" }
					: event.key === "ArrowRight"
						? { move: "right" }
						: event.key === "ArrowUp" || event.key === " "
							? { jump: true }
							: null;
			if (!input) return;
			// Space scrolls the page and the arrows move it sideways, and the
			// page is the game.
			event.preventDefault();
			onPress(input);
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onPress, live]);
}

/**
 * Whether this browser will actually give us a 3D context.
 *
 * Asked once and never again: creating a context is not free, and the answer
 * cannot change while a run is on. A refusal is not an error — a school-managed
 * tablet with hardware acceleration turned off is a real device with a real
 * child on it, and it gets the flat tunnel.
 */
function useWebGl(): boolean {
	return useMemo(() => {
		try {
			const canvas = document.createElement("canvas");
			return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
		} catch {
			return false;
		}
	}, []);
}
